import request from "@common/request";
import { retryNon4xx } from "@common/queryError";
import { Account, Clients, MxAccount, Profile } from "@modules/forme/types";
import { PaginatedResponse, OrionClient } from "@modules/forme/types";
import { useQuery } from "react-query";
import { ClientInfoComputedFields, HouseholdInfo } from "./types";
import { useMemo } from "react";
import { computedFields } from "./helper";

const useAdvisors = () => {
  return useQuery("advisors", () =>
    request<Forme.PaginatedResponse<Forme.Advisor>>("GET", "/api/advisor", {
      size: 1000,
    })
  );
};

const useCurrentAdvisor = ({ enabled }: { enabled?: boolean } = {}) => {
  return useQuery(
    ["currentAdvisor"],
    () =>
      // Skip impersonation: this call should always return the advisor's own
      // identity, even when the advisor is impersonating a client.
      request<Forme.Advisor>(
        "GET",
        "/api/advisor/me",
        undefined,
        undefined,
        true
      ),
    { enabled }
  );
};

const useFormeHouseholds = (search?: string, filters?: string) => {
  // Normalize empty strings to undefined for consistent query keys
  const searchParam = search && search.trim() ? search : undefined;
  const filtersParam = filters && filters.trim() ? filters : undefined;
  return useQuery(
    ["formeHouseholds", searchParam, filtersParam],
    () => {
      const params = new URLSearchParams({ SOURCE: "FORME" });
      if (searchParam) {
        params.append("search", searchParam);
      }
      if (filtersParam) {
        params.append("filters", filtersParam);
      }
      return request<Forme.PaginatedResponse<Forme.HouseholdWithClientUsers>>(
        "GET",
        `/api/advisor/households?${params.toString()}`
      );
    },
    {
      keepPreviousData: true,
    }
  );
};

// One household by id, for callers that have a route param rather than a
// search. Deliberately NOT derived from useFormeHouseholds: that query is
// search-scoped and paginated, so a household outside the current search simply
// is not in its cache.
const useFormeHousehold = (householdId?: string) =>
  useQuery(
    ["formeHousehold", householdId],
    () =>
      request<Forme.HouseholdWithClientUsers>(
        "GET",
        `/api/advisor/households/${householdId}`
      ),
    {
      enabled: !!householdId,
    }
  );

const useOrionHouseholds = () => {
  return useQuery("orionHouseholds", async () => {
    let allHouseholds: OrionClient[] = [];
    let nextPageToken: string | null = null;
    let previousPageToken: string | null = null;

    do {
      const url: string = nextPageToken
        ? `/api/advisor/households?SOURCE=ORION&nextPageToken=${encodeURIComponent(
            nextPageToken
          )}`
        : "/api/advisor/households?SOURCE=ORION";

      const response = await request<PaginatedResponse<OrionClient>>(
        "GET",
        url
      );
      allHouseholds = [...allHouseholds, ...response.data];

      previousPageToken = nextPageToken;
      nextPageToken = response.paging?.nextPageToken || null;

      // Break if nextPageToken hasn't changed (prevents infinite loop)
      if (nextPageToken && nextPageToken === previousPageToken) {
        console.warn("Breaking pagination loop: nextPageToken did not advance");
        break;
      }
    } while (nextPageToken && nextPageToken.trim());

    return {
      data: allHouseholds,
      paging: { nextPageToken: null },
    };
  });
};

const useHouseholdClients = (
  { householdId }: { householdId: string },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any
) => {
  return useQuery(["householdClients", householdId], () =>
    request<Clients>("GET", "/api/advisor/clients", { householdId }, options)
  );
};

const useClients = ({ offset, limit }: { offset: number; limit: number }) => {
  return useQuery(["clients", offset], () =>
    request<{
      clients: Forme.ClientUser[];
      totalCount: number;
    }>("GET", "/api/advisor/clients", { offset, limit })
  );
};

const useClientProfile = ({ clientId }: { clientId: string | undefined }) => {
  return useQuery(["clientProfile", clientId], () =>
    request<Profile>("GET", `/api/advisor/clients/${clientId}/profile`, {
      enabled: !!clientId,
    })
  );
};

const useClientByUserId = ({ userId }: { userId?: string }) => {
  return useQuery(
    ["clients", userId],
    () =>
      request<{
        client: Forme.ClientUser;
      }>("GET", `/api/advisor/clients?userId=${userId}`, {}, {}, true),
    {
      enabled: !!userId,
    }
  );
};

const useHouseholdTasks = (householdId: string | undefined) => {
  return useQuery(
    ["tasks", householdId],
    () =>
      request<Forme.EnrichedTask[]>(
        "GET",
        `/api/advisor/households/${householdId}/tasks`
      ),
    {
      enabled: !!householdId,
    }
  );
};
const useHouseholdOrionAccounts = (
  householdId: string | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) => {
  return useQuery(
    ["orionAccounts", householdId],
    () =>
      request<Account[]>(
        "GET",
        `/api/advisor/households/${householdId}/accounts?type=orion`
      ),
    {
      enabled: enabled && !!householdId,
      // Don't retry a 4xx (e.g. 403) through backoff — it's deterministic and,
      // since this query gates the Client 360 tabs, retries sit in the critical
      // path. (Shared hook: leaving cache freshness — staleTime — as-is so the
      // Command Center sidebar's semantics don't change.)
      retry: retryNon4xx(),
    }
  );
};

const useHouseholdMxAccounts = (householdId: string | undefined) => {
  return useQuery(
    ["mxAccounts", householdId],
    () =>
      request<MxAccount[]>(
        "GET",
        `/api/advisor/households/${householdId}/accounts?type=mx`
      ),
    {
      enabled: !!householdId,
      retry: retryNon4xx(),
    }
  );
};

const useHouseholdInfo = (householdId: string | undefined) => {
  const { data, ...result } = useQuery(
    ["householdInfo", householdId],
    () =>
      request<HouseholdInfo>(
        "GET",
        `/api/advisor/households/${householdId}/info`
      ),
    {
      enabled: !!householdId,
    }
  );

  const householdData: HouseholdInfo | undefined = useMemo(() => {
    if (data) {
      // Apply computed fields
      const computedValues = Object.keys(
        computedFields
      ).reduce<ClientInfoComputedFields>((fields, k) => {
        const key = k as keyof typeof computedFields;
        const compute = computedFields[key];
        return {
          ...fields,
          [key]: compute?.(data),
        };
      }, {} as ClientInfoComputedFields);

      return {
        ...data,
        client: {
          ...data.client,
          ...computedValues.client,
        },
        ...(data.coClient && {
          coClient: {
            ...data.coClient,
            ...computedValues.coClient,
          },
        }),
      };
    }
  }, [data]);

  return {
    data: householdData,
    ...result,
  };
};

export {
  useAdvisors,
  useCurrentAdvisor,
  useFormeHouseholds,
  useFormeHousehold,
  useOrionHouseholds,
  useClients,
  useClientByUserId,
  useHouseholdOrionAccounts,
  useHouseholdClients,
  useHouseholdTasks,
  useClientProfile,
  useHouseholdMxAccounts,
  useHouseholdInfo,
};
